/**
 * Uygulama düzeyinde analiz akışı: dosya → çıkarım → skorlama.
 */

import type { ExtractedDocument } from './types';
import { extractDocument, detectFormat, type ExtractionProgress } from './extract';
import { extractPlainText } from './extract/plain';
import { analyzeDocument, type AnalysisResult } from './report/engine';

export type Stage = 'idle' | 'extracting' | 'analyzing' | 'done' | 'error';

export interface PipelineProgress {
	stageKey: string;
	stageParams?: Record<string, string | number>;
	ratio: number;
	phase: 'extract' | 'analyze';
	page?: number;
	total?: number;
}

export function isSupportedFile(file: File): boolean {
	if (detectFormat(file.name, file.type)) return true;
	return /\.(txt|md|markdown)$/i.test(file.name) || file.type === 'text/plain' || file.type === 'text/markdown';
}

export async function extractFile(
	file: File,
	onProgress: (p: PipelineProgress) => void = () => {}
): Promise<ExtractedDocument> {
	if (detectFormat(file.name, file.type)) {
		return extractDocument(file, (p: ExtractionProgress) =>
			onProgress({
				stageKey: p.stageKey,
				stageParams: p.stageParams,
				ratio: p.ratio * 0.8,
				phase: 'extract',
				page: p.page,
				total: p.total
			})
		);
	}
	return extractPlainText(file, (p: ExtractionProgress) =>
		onProgress({
			stageKey: p.stageKey,
			stageParams: p.stageParams,
			ratio: p.ratio * 0.8,
			phase: 'extract'
		})
	);
}

export async function analyzeFile(
	file: File,
	onProgress: (p: PipelineProgress) => void = () => {}
): Promise<{ analysis: AnalysisResult; doc: ExtractedDocument }> {
	onProgress({ stageKey: 'proc.stage.preparing', ratio: 0.02, phase: 'extract' });
	const doc = await extractFile(file, onProgress);
	onProgress({ stageKey: 'proc.stage.analyzing', ratio: 0.84, phase: 'analyze' });

	// UI'nin boyanmasına izin ver
	await new Promise((r) => setTimeout(r, 30));
	const analysis = analyzeDocument(doc);
	onProgress({ stageKey: 'proc.stage.report', ratio: 0.98, phase: 'analyze' });
	return { analysis, doc };
}
